import React, { useState, useEffect, useRef } from 'react';
import './SpringBootDeepDive.css';
import { sections } from './springBootData';
import { useInterview } from './useInterviewContext';

function AccordionItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`sb-accordion-item ${open ? ' open' : ''}`}>
            <button className="sb-accordion-trigger" onClick={() => setOpen(o => !o)}>
                <span className="sb-accordion-q">{q}</span>
                <span className="sb-accordion-icon" role="img" aria-label="expand">+</span>
            </button>
            <div className="sb-accordion-body">
                <div className="sb-accordion-content">
                    <div className="sb-answer-label"><span role="img" aria-label="check">✅</span> Model Answer</div>
                    <div className="sb-answer-text" dangerouslySetInnerHTML={{
                        __html: a.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>')
                    }} />
                </div>
            </div>
        </div>
    );
}

export default function SpringBootDeepDive() {
    const { toggleCoach, setActiveTopic } = useInterview();
    const [activeId, setActiveId] = useState('core');
    const refs = useRef({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const obs = new IntersectionObserver(
            es => es.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }),
            { rootMargin: '-15% 0px -75% 0px' }
        );
        Object.values(refs.current).forEach(el => el && obs.observe(el));
        return () => obs.disconnect();
    }, []);

    const scrollTo = id => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="sb-page">
            <div className="sb-topbar">
                <button className="sb-back-btn" onClick={() => { window.location.hash = 'swe'; }}>← Back to SWE</button>
                <div style={{ textAlign: 'center' }}>
                    <div className="sb-topbar-title"><span role="img" aria-label="spring-leaf">🍃</span> Spring Boot Deep Dive</div>
                    <div className="sb-topbar-subtitle">Core · Beans · Web · Data · Security</div>
                </div>
                <div style={{ width: 130 }} />
            </div>

            <div className="sb-body">
                <nav className="sb-sidebar">
                    <div className="sb-sidebar-title">Modules</div>
                    {sections.map(n => (
                        <button key={n.id} className={`sb-nav-item ${activeId === n.id ? ' active' : ''}`} onClick={() => scrollTo(n.id)}>
                            {n.icon} {n.title}
                        </button>
                    ))}
                </nav>

                <main className="sb-main">
                    {sections.map((s) => (
                        <section key={s.id} id={s.id} className="sb-section" ref={el => (refs.current[s.id] = el)}>
                            <div className="sb-section-header">
                                <div className="sb-section-icon" style={{ background: s.bg }}>{s.icon}</div>
                                <div className="sb-section-meta">
                                    <h2>{s.icon} {s.title} <span className="sb-tagline">— {s.tagline}</span></h2>
                                </div>
                                <button
                                    className="sb-practice-btn"
                                    onClick={() => {
                                        setActiveTopic({ ...s, type: 'swe' });
                                        toggleCoach(true);
                                    }}
                                >
                                    🤖 Practice with AI
                                </button>
                            </div>

                            <div className="sb-simple-box">
                                <div className="sb-simple-label"><span role="img" aria-label="book">📖</span> Overview</div>
                                <p>{s.simple}</p>
                            </div>

                            <div className="sb-key-points">
                                <div className="sb-simple-label"><span role="img" aria-label="target">🎯</span> Key Points</div>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {s.keyPoints.map((p, i) => (
                                        <li key={i} style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.75rem', color: '#94a3b8' }}>
                                            <span style={{ color: '#6DB33F' }}>•</span> {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="sb-code-block">
                                <div className="sb-code-label"><span role="img" aria-label="laptop">💻</span> Code Example</div>
                                <pre>{s.code}</pre>
                            </div>

                            <div className="sb-qa-section">
                                <div className="sb-simple-label"><span role="img" aria-label="question">❓</span> Interview Q&A</div>
                                <div className="sb-accordion">
                                    {s.qa.map((q, i) => (
                                        <AccordionItem key={i} q={q.q} a={q.a} />
                                    ))}
                                </div>
                            </div>
                        </section>
                    ))}
                </main>
            </div>
        </div>
    );
}
